import { Link } from "@tanstack/react-router";
import {
  ColumnDef,
  ColumnHelper,
  ColumnMeta,
  ColumnSort,
  DisplayColumnDef,
  ExpandedRow,
  Row,
  VisibilityState,
} from "@tanstack/react-table";
import {
  ControlCode,
  ControlCodes,
  ExceptedCategory,
  ExitCode,
  ExitCodeGroup,
  NonArchPlatform,
  PartnerUsersDevice,
  Platform,
  TimeFormats,
  ValidControlCategory,
  ValidControlCode,
  capitalize,
  cn,
  differenceInDays,
  displayPlatform,
  formatDate,
  getControlCategoryName,
  parseDate,
  pluralize,
  titleCase,
} from "@theprelude/core";
import {
  AlertIcon,
  ArrowRight,
  Checkbox,
  CheckmarkIcon,
  CloseIcon,
  GlobeIcon,
  OrderIcon,
  RefreshIcon,
  RestartIcon,
  TagIcon,
  Truncate,
  VSTIndicatorIcon,
  PlatformIcon,
  ExceptionIcon,
  ExternalLinkIcon,
} from "@theprelude/ds";
import pick from "lodash/pick";
import type { JSX } from "react";
import { Highlight } from "@/components/highlight";
import { Filter } from "@/lib/filter";
import {
  getEncodedOdataValue,
  getOdataId,
  OdataDefinition,
  OdataOperator,
} from "@/lib/odata";
import { ExpectedOutcome, ExpectedResult, OptionalODPFields } from "@/lib/odp";
import { PolicySettingsDocLink } from "../deep-links";
import { DefaultTooltip } from "../default-tooltip";
import { ExternalLink } from "../external-link";
import {
  AdditionalFilter,
  cloudAccountIdFilter,
  cloudInstanceIdFilter,
  controlCloudAccountIdFilter,
  controlCloudInstanceIdFilter,
  controlCloudRegionFilter,
  deviceTypeFilter,
  domainFilter,
  durationStartTimeFilter,
  emailFilter,
  exceptedFilter,
  hasAgentFilter,
  hostNameFilter,
  inboxTypeFilter,
  lastScanFilter,
  lastSeenFilter,
  macAddressFilter,
  ownershipFilter,
  platformFilter,
  principalNameFilter,
  riskFilter,
  serialNumberFilter,
  startTimeFilter,
  statusFilter,
  userDevicesFilter,
  userTypeFilter,
  versionFilter,
} from "../filter-form/additional-filters";
import { FormFilter } from "../filter-form/filter-form";
import { hasFeatures } from "../flag/flag";
import {
  SelectEndpointIcon,
  SelectTestIcon,
  SelectThreatIcon,
} from "../overlay-icons";
import { Tags } from "../tags";
import { MDPAction } from "./action-buttons";
import { ExpandableContentButton } from "./expandable-content-button";
import {
  BenchmarkColumnValue,
  ExpectedOutcomeBlock,
  ExpectedProtectionLevel,
  PercentageValue,
  PersonalExpectedOutcomeBlock,
  ProtectionStatus,
} from "./protection-level";
import { ScheduleStatus } from "./schedule-status";
import { None, Unavailable } from "./unavaliable";

export type CellInfo<T, K = any> = {
  getValue: () => T;
  row: ExpandedRow & {
    original: K;
  };
  table: {
    getState: () => {
      globalFilter: string;
    };
  };
};

function sortColumnLists<T>(rowA: Row<T>, rowB: Row<T>, columnId: string) {
  const arrayA: T[] = rowA.getValue(columnId);
  const arrayB: T[] = rowB.getValue(columnId);

  for (const [i, valueA] of arrayA.entries()) {
    const valueB = arrayB[i];
    if (!valueB) return 0;
    if (valueA < valueB) return -1;
    if (valueA > valueB) return 1;
  }

  if (arrayA.length < arrayB.length) return 1;
  if (arrayA.length > arrayB.length) return -1;

  return 0;
}

function sortColumnObjects<T>(
  rowA: Row<T>,
  rowB: Row<T>,
  columnId: string,
): number {
  const objectA: { [key: string]: T } = rowA.getValue(columnId);
  const objectB: { [key: string]: T } = rowB.getValue(columnId);

  if (!objectA || !objectB) return 1;

  const keysA = Object.keys(objectA);
  const keysB = Object.keys(objectB);

  for (const key of keysA) {
    if (objectB.hasOwnProperty(key)) {
      const valueA = objectA[key];
      const valueB = objectB[key];

      if (valueA === undefined || valueB === undefined) {
        if (valueA !== undefined) return -1;
        if (valueB !== undefined) return 1;
      }

      if (!valueA || !valueB) return 0;
      if (valueA < valueB) return 1;
      if (valueA > valueB) return -1;
    } else {
      return -1;
    }
  }

  if (keysA.length < keysB.length) return -1;
  if (keysA.length > keysB.length) return 1;

  return 0;
}

function getStatus(exitCode: ExitCode) {
  const isPass = (ExitCodeGroup.PROTECTED as unknown as number[]).includes(
    exitCode,
  );
  const isFail = (ExitCodeGroup.UNPROTECTED as unknown as number[]).includes(
    exitCode,
  );
  const isError = (ExitCodeGroup.ERROR as unknown as number[]).includes(
    exitCode,
  );
  const isNotRelevant = (
    ExitCodeGroup.NOT_RELEVANT as unknown as number[]
  ).includes(exitCode);

  if (isPass) return "prevented";
  if (isFail) return "not prevented";
  if (isError) return "error";
  if (isNotRelevant) return "not relevant";
}

export const widths = {
  full: "xl:min-w-[500px] max-w-0 min-w-[300px]",
  quickview: "w-full min-w-[100px] max-w-0",
  xs: "min-w-[32px] max-w-[32px]",
  sm: "min-w-[105px] max-w-[105px]",
  md: "min-w-[120px] max-w-[120px]",
  lg: "min-w-[140px] max-w-[140px]",
} as const;

export type ColumnFilter = {
  operators: OdataOperator[];
  type: "text" | "number" | "duration" | "date" | "boolean";
  label: string;
  group?: string;
  odata: OdataDefinition;
  cast?: (filter: FormFilter) => Filter;
  parse?: (filter: Filter) => FormFilter;
};

export type Column<T, K = any, ID extends string = string> = Omit<
  DisplayColumnDef<any, T>,
  "cell" | "id" | "meta"
> & {
  id: ID;
  cell?: (info: CellInfo<T, K>) => JSX.Element;
  meta?: Omit<ColumnMeta<T, K>, "exportFn"> & {
    exportFn?: (data: T) => string;
  };
};

const getColumnFilter = (filter: AdditionalFilter): ColumnFilter =>
  pick(filter, [
    "type",
    "label",
    "operators",
    "odata",
    "cast",
    "parse",
    "isArray",
  ]);

export const benchmarkColumn: Column<number | undefined> = {
  id: "benchmark",
  enableSorting: true,
  sortUndefined: "last",
  cell: (info) => <BenchmarkColumnValue status={info.getValue()} />,
  meta: {
    label: "Benchmark",
    widths: ["120px", "1fr"],
  },
};

export const protectedColumn: Column<number | undefined> = {
  id: "protected",
  enableSorting: true,
  sortUndefined: "last",
  cell: (info) => <PercentageValue status={info.getValue()} />,
  meta: {
    label: "Protected",
    widths: ["130px", "1fr"],
  },
};

export const testProtectedColumn: typeof protectedColumn = {
  ...protectedColumn,
  meta: {
    ...protectedColumn.meta,
    authorize: (account) => !hasFeatures(account, ["observed_detected"]),
  },
};

export const lastResultStatusColumn: Column<number | undefined> = {
  id: "protected",
  enableSorting: true,
  sortUndefined: "last",
  cell: (info) => <ProtectionStatus status={info.getValue()} />,
  meta: {
    label: "Latest Result",
    widths: ["120px", "1fr"],
  },
};

export const edrColumn: Column<string | undefined> = {
  id: "control",
  enableSorting: true,
  sortUndefined: "last",
  filterFn: (row, col, value) => {
    if (!value && !row.getValue(col)) {
      return true;
    }
    if (value === row.getValue(col)) {
      return true;
    }
    return false;
  },
  cell: (info) => {
    const edr = info.getValue();
    if (edr === undefined) {
      return <None />;
    }
    return (
      <Truncate text={edr}>
        <Highlight text={edr} highlight={info.table.getState().globalFilter} />
      </Truncate>
    );
  },
  meta: {
    label: "EDR",
    widths: ["80px", "1.25fr"],
  },
};

export const hostColumn: Column<string, { endpoint_id: string }> = {
  id: "host",
  enableSorting: true,
  cell: (info) => (
    <SelectEndpointIcon id={info.row.original.endpoint_id}>
      <Truncate text={info.getValue()}>
        <Highlight
          text={info.getValue()}
          highlight={info.table.getState().globalFilter}
        />
      </Truncate>
    </SelectEndpointIcon>
  ),
  meta: {
    label: "Host",
    widths: ["100px", "3fr"],
  },
};

export const hostSmallColumn: typeof hostColumn = {
  ...hostColumn,
  meta: { ...hostColumn.meta, widths: ["100px", "3fr"] as const },
};

export const platformColumn: Column<Platform | undefined> = {
  id: "dos",
  enableSorting: true,
  sortUndefined: "last",
  cell: (info) => {
    const platform = info.getValue();
    if (platform === undefined) {
      return <Unavailable />;
    }
    return (
      <div className="flex min-w-0 items-center gap-1">
        <PlatformIcon
          className="h-4 w-4 text-secondary-foreground"
          platform={platform}
        />
        <Truncate text={displayPlatform(platform)}>
          <Highlight
            text={displayPlatform(platform)}
            highlight={info.table.getState().globalFilter}
          />
        </Truncate>
      </div>
    );
  },
  meta: {
    label: "Platform",
    widths: ["105px", "1fr"],
  },
};

export const osColumn: Column<string | undefined> = {
  id: "os",
  enableSorting: true,
  sortUndefined: "last",
  cell: (info) => {
    const os = info.getValue();
    if (os === undefined) {
      return <Unavailable />;
    }
    return (
      <Truncate text={os}>
        <Highlight text={os} highlight={info.table.getState().globalFilter} />
      </Truncate>
    );
  },
  meta: {
    label: "OS",
    widths: ["105px", "1.5fr"],
  },
};

export const supportedPlatformsColumn: Column<NonArchPlatform[]> = {
  id: "supported_platforms",
  enableSorting: true,
  sortingFn: sortColumnLists,
  cell: (info) => {
    const supported_platforms = info.getValue();
    if (supported_platforms.length === 0) {
      return <Unavailable />;
    }

    return (
      <div className="flex items-center gap-2">
        {supported_platforms.map((platform) => (
          <PlatformIcon
            className="size-4 text-secondary-foreground"
            platform={platform}
            key={platform}
          />
        ))}
      </div>
    );
  },
  meta: {
    label: "Platforms",
    widths: ["105px", "1fr"],
    exportFn: (data) => data.join(", "),
  },
};

export const checkboxColumn: DisplayColumnDef<any> = {
  id: "select",
  enableSorting: false,
  enableHiding: false,
  header: ({ table }) => (
    <Checkbox
      checked={table.getIsAllRowsSelected()}
      onCheckedChange={(e) => table.toggleAllRowsSelected(e === true)}
    />
  ),
  cell: ({ row }) => (
    <div className="flex items-center">
      <Checkbox
        checked={row.getIsSelected()}
        disabled={!row.getCanSelect()}
        onCheckedChange={row.getToggleSelectedHandler()}
      />
    </div>
  ),
  meta: {
    widths: ["32px", "32px"],
  },
};

export const sourceIdColumn: Column<string | undefined, { source?: string }> = {
  id: "source_id",
  enableSorting: true,
  sortUndefined: "last",
  cell: (info) => {
    const value = info.getValue()?.toUpperCase();
    if (value === undefined) {
      return <None />;
    }

    return (
      <div className="flex min-w-0 items-center gap-1">
        <Truncate text={value}>
          <Highlight
            text={value.toUpperCase()}
            highlight={info.table.getState().globalFilter}
          />
        </Truncate>

        {info.row.original.source && (
          <ExternalLink href={info.row.original.source} />
        )}
      </div>
    );
  },
  meta: {
    label: "Source ID",
    widths: ["120px", "1fr"],
  },
};
// TODO: when would this happen as undefined?

export const threatNameColumn: Column<
  string | undefined,
  { threatId: string | undefined }
> = {
  id: "threat",
  enableSorting: true,
  sortUndefined: "last",
  cell: (info) => {
    const threat = info.getValue();
    if (!threat || !info.row.original.threatId) {
      return <None />;
    }
    return (
      <SelectThreatIcon id={info.row.original.threatId}>
        <Truncate text={threat}>
          <Highlight
            text={threat}
            highlight={info.table.getState().globalFilter}
          />
        </Truncate>
      </SelectThreatIcon>
    );
  },
  meta: {
    label: "Threat",
    widths: ["100px", "5fr"],
  },
};

export const threatNameSmallColumn: typeof threatNameColumn = {
  ...threatNameColumn,
  meta: { ...threatNameColumn.meta, widths: ["100px", "5fr"] as const },
};

export const scheduledColumn: Column<boolean | undefined> = {
  id: "schedule-status",
  sortUndefined: "last",
  enableSorting: true,
  cell: (info) => <ScheduleStatus status={info.getValue()} />,
  meta: {
    label: "Scheduled",
    widths: ["120px", "1fr"],
    exportFn: (data) => (data ? "Yes" : "No"),
  },
};

export const publishedColumn: Column<string | undefined> = {
  id: "published",
  enableSorting: true,
  sortUndefined: "last",
  cell: (info) => {
    const date = info.getValue();
    if (date === undefined) {
      return <Unavailable />;
    }
    const formattedDate = formatDate(parseDate(date), TimeFormats.DATE_SHORT);
    return (
      <div>
        <Highlight
          text={formattedDate}
          highlight={info.table.getState().globalFilter}
        />
      </div>
    );
  },
  meta: {
    label: "Published",
    widths: ["105px", "1fr"],
    exportFn: (data) =>
      data ? formatDate(parseDate(data), TimeFormats.DATE_SHORT) : "",
  },
};

export const authorColumn: Column<string | undefined> = {
  id: "author",
  enableSorting: true,
  sortUndefined: "last",
  cell: (info) => {
    const author = info.getValue();
    if (!author) {
      return <Unavailable />;
    }
    return <Truncate text={author}>{author}</Truncate>;
  },
  meta: {
    label: "Author",
    widths: ["120px", "1fr"],
  },
};

export const userEmailColumn: Column<string | undefined> = {
  id: "email",
  enableSorting: true,
  sortUndefined: "last",
  cell: (info) => {
    const email = info.getValue();
    if (!email) {
      return <Unavailable />;
    }
    return (
      <Link
        to={window.location.pathname}
        params={(prev) => prev}
        search={(prev) => ({
          ...prev,
          overlay: ["user", { email }],
        })}
        className="flex items-center gap-1"
      >
        <Truncate text={email}>{email}</Truncate>
        <ExternalLinkIcon />
      </Link>
    );
  },
  meta: {
    label: "Email",
    widths: ["250px", "2fr"],
    filter: getColumnFilter(emailFilter),
  },
};

export const inboxEmailColumn: Column<string | undefined> = {
  id: "email",
  enableSorting: true,
  sortUndefined: "last",
  cell: (info) => {
    const email = info.getValue();
    if (!email) {
      return <Unavailable />;
    }
    return <Truncate text={email}>{email}</Truncate>;
  },
  meta: {
    label: "Email",
    widths: ["250px", "2fr"],
    filter: getColumnFilter(emailFilter),
  },
};

export const userDevicesColumn: Column<PartnerUsersDevice[] | undefined> = {
  id: "devices",
  enableSorting: true,
  sortUndefined: "last",
  cell: (info) => {
    const devices = info.getValue() ?? [];
    const [device] = devices;
    if (!device) {
      return <None />;
    }
    if (devices.length === 1) {
      return <Truncate text={device.hostname}>{device.hostname}</Truncate>;
    }
    return (
      <ExpandableContentButton
        row={info.row}
        label={`${devices.length} Devices`}
      />
    );
  },
  meta: {
    label: "Devices",
    widths: ["120px", "1fr"],
    filter: getColumnFilter(userDevicesFilter),
  },
};

// TODO: when would this happen as undefined?
export const testIdColumn: Column<string | undefined> = {
  id: "test_id",
  enableSorting: true,
  sortUndefined: "last",
  cell: (info) => {
    const id = info.getValue();
    if (id === undefined) {
      return <Unavailable />;
    }
    return (
      <Truncate text={id}>
        <Highlight text={id} highlight={info.table.getState().globalFilter} />
      </Truncate>
    );
  },
  enableGlobalFilter: true,
  meta: {
    label: "Test ID",
  },
};

export const threatIdColumn: Column<string> = {
  id: "threat_id",
  enableSorting: true,
  cell: (info) => {
    return (
      <Truncate text={info.getValue()}>
        <Highlight
          text={info.getValue()}
          highlight={info.table.getState().globalFilter}
        />
      </Truncate>
    );
  },
  enableGlobalFilter: true,
  meta: {
    label: "Threat ID",
  },
};

// TODO: when would this happen as undefined?
export const testNameColumn: Column<
  string | undefined,
  { testId: string | undefined; account_id: string | undefined }
> = {
  id: "test-name",
  enableSorting: true,
  sortUndefined: "last",
  cell: (info) => {
    const testName = info.getValue();
    if (testName === undefined) {
      return <Unavailable />;
    }
    if (info.row.original.testId) {
      return (
        <SelectTestIcon id={info.row.original.testId}>
          <Truncate text={testName}>
            <Highlight
              text={testName}
              highlight={info.table.getState().globalFilter}
            />
          </Truncate>
          {info.row.original.account_id === "prelude" && <VSTIndicatorIcon />}
        </SelectTestIcon>
      );
    }
    return (
      <Truncate text={testName}>
        <Highlight
          text={testName}
          highlight={info.table.getState().globalFilter}
        />
      </Truncate>
    );
  },
  meta: {
    label: "Verified Security Test",
    widths: ["210px", "5fr"],
  },
};

export const testNameSmallColumn: typeof testNameColumn = {
  ...testNameColumn,
  meta: { ...testNameColumn.meta, widths: ["220px", "5fr"] as const },
};

export const orderColumn: Column<number | string> = {
  id: "order",
  enableHiding: false,
  header: () => <OrderIcon className="text-primary-foreground" />,
  enableSorting: true,
  meta: {
    hideSorting: true,
    widths: ["32px", "32px"],
  },
  cell: (info) => {
    const number = info.getValue();
    return <span className="text-xs text-secondary-foreground">{number}</span>;
  },
};

export const techniqueColumn: Column<string | undefined> = {
  id: "technique",
  enableSorting: true,
  sortUndefined: "last",
  cell: (info) => {
    const technique = info.getValue();
    if (!technique) {
      return <None />;
    }

    return (
      <span className="flex items-center gap-1">
        <Highlight
          text={technique}
          highlight={info.table.getState().globalFilter}
        />
        <ExternalLink
          href={`https://attack.mitre.org/techniques/${technique.replaceAll(
            ".",
            "/",
          )}`}
        />
      </span>
    );
  },
  meta: {
    widths: ["120px", "1fr"],
    label: "Technique",
  },
  enableGlobalFilter: true,
};

export const threatsColumn: Column<
  {
    id: string;
    name: string;
  }[]
> = {
  id: "threats",
  enableSorting: true,
  cell: (info) => {
    const threats = info.getValue();

    if (threats.length === 0) {
      return <None />;
    }

    if (threats.length === 1) {
      const threat = threats[0];

      if (!threat) {
        return <None />;
      }

      return (
        <SelectThreatIcon id={threat.id}>
          <Truncate text={threat.name}>{threat.name}</Truncate>
        </SelectThreatIcon>
      );
    }
    return <div>{threats.length} threats</div>;
  },
  meta: {
    label: "Threats",
    widths: ["150px", "3fr"],
    exportFn: (data) => data.map((d) => d.name).join(", "),
  },
};

export const createdColumn: Column<string | undefined> = {
  id: "created",
  sortUndefined: "last",
  enableSorting: true,
  cell: (info) => {
    const date = info.getValue();
    if (date === undefined) {
      return <Unavailable />;
    }
    const formattedDate = formatDate(parseDate(date), TimeFormats.DATE_SHORT);
    return (
      <div>
        <Highlight
          text={formattedDate}
          highlight={info.table.getState().globalFilter}
        />
      </div>
    );
  },
  meta: {
    label: "Created",
    widths: ["105px", "1fr"],
    exportFn: (data) =>
      data ? formatDate(parseDate(data), TimeFormats.DATE_SHORT) : "",
  },
};

export const timeColumn: Column<string | undefined> = {
  id: "time",
  sortUndefined: "last",
  enableSorting: true,
  cell: (info) => {
    const date = info.getValue();
    if (date === undefined) {
      return <Unavailable />;
    }
    const formattedDate = formatDate(parseDate(date), TimeFormats.DATE_FIRST);
    return (
      <Truncate text={formattedDate}>
        <Highlight
          text={formattedDate}
          highlight={info.table.getState().globalFilter}
        />
      </Truncate>
    );
  },
  meta: {
    label: "Time",
    widths: ["180px", "1.2fr"],
    exportFn: (data) =>
      data ? formatDate(parseDate(data), TimeFormats.DATE_FIRST) : "",
  },
};

export const lastSeenColumn: Column<string> = {
  id: "last_seen",
  enableSorting: true,
  cell: (info) => {
    if (!info.getValue()) {
      return <Unavailable />;
    }
    return <Truncate text={info.getValue()}>{info.getValue()}</Truncate>;
  },
  meta: {
    label: "Last Seen",
    widths: ["110px", "1fr"],
  },
};

export const endpointIdColumn: Column<string> = {
  id: "endpoint_id",
  enableSorting: true,
  cell: (info) => (
    <Truncate text={info.getValue()}>
      <Highlight
        text={info.getValue()}
        highlight={info.table.getState().globalFilter}
      />
    </Truncate>
  ),
  enableGlobalFilter: true,
  meta: {
    label: "Endpoint ID",
  },
};

export const tagsColumn: Column<string[]> = {
  id: "tags",
  enableSorting: true,
  sortingFn: (a, b, columnId) => sortColumnLists(a, b, columnId),
  cell: (info) => {
    const tags = info.getValue();
    return tags.length === 0 ? (
      <None />
    ) : (
      <Tags tags={info.getValue()} limit={1} />
    );
  },
  meta: {
    widths: ["140px", "1fr"],
    label: "Tags",
    exportFn: (data) => data.join(", "),
  },
};

export const policyColumn: Column<
  string | undefined,
  { policy_name: string | undefined }
> = {
  id: "policy",
  enableSorting: true,
  sortUndefined: "last",
  enableGlobalFilter: true,
  cell: (info) => {
    const display = info.row.original.policy_name ?? info.getValue();
    if (!display) {
      return <None />;
    }
    return (
      <Truncate text={display}>
        <Highlight
          text={display}
          highlight={info.table.getState().globalFilter}
        />
      </Truncate>
    );
  },
  meta: {
    label: "Policy",
    widths: ["80px", "2fr"],
  },
};

export const frequencyColumn: Column<string | undefined> = {
  id: "frequency",
  enableSorting: true,
  sortUndefined: "last",
  cell: (info) => {
    const frequency = info.getValue();

    if (frequency === undefined) {
      return <None />;
    }

    return (
      <div className="flex items-center gap-1">
        <RestartIcon />
        <Truncate text={frequency}>
          <Highlight
            text={frequency}
            highlight={info.table.getState().globalFilter}
          />
        </Truncate>
      </div>
    );
  },
  meta: {
    label: "Frequency",
    widths: ["170px", "1fr"],
  },
};

const getTarget = (tags: string[] | null) => {
  if (tags === null) {
    return "All hosts";
  }

  if (tags.length === 1) {
    return tags[0];
  }

  return `${tags.length} tags`;
};

export const targetColumn: Column<string[] | null | undefined> = {
  id: "target",
  enableSorting: true,
  sortUndefined: "last",
  cell: (info) => {
    const target = info.getValue();

    if (target === undefined) {
      return <None />;
    }

    const targetName = getTarget(target);

    return (
      <div className="flex items-center gap-1">
        {targetName === "All hosts" ? <GlobeIcon /> : <TagIcon />}
        {targetName}
      </div>
    );
  },
  meta: {
    label: "Target",
    widths: ["140px", "1fr"],
    exportFn: (data) => {
      if (data === null) {
        return "All hosts";
      }

      return data?.join(", ") ?? "";
    },
  },
};

export const observedColumn: Column<number | undefined> = {
  id: "observed",
  sortUndefined: "last",
  cell: (info) => <PercentageValue status={info.getValue()} />,
  meta: {
    label: "Observed",
    widths: ["105px", "1fr"],
    authorize: (account) =>
      hasFeatures(account, ["observed_detected"]) &&
      account.controls.some((c) => c.id === ControlCodes.CROWDSTRIKE),
  },
};

export const detectedColumn: Column<number | undefined> = {
  id: "detected",
  sortUndefined: "last",
  cell: (info) => <PercentageValue status={info.getValue()} />,
  meta: {
    label: "Detected",
    widths: ["105px", "1fr"],
    authorize: (account) =>
      hasFeatures(account, ["observed_detected"]) &&
      account.controls.some((c) => c.id === ControlCodes.CROWDSTRIKE),
  },
};

export const preventedColumn: Column<number | undefined> = {
  id: "prevented",
  sortUndefined: "last",
  cell: (info) => <PercentageValue status={info.getValue()} />,
  meta: {
    label: "Prevented",
    widths: ["105px", "1fr"],
    authorize: (account) => hasFeatures(account, ["observed_detected"]),
  },
};

export const preventedStatusColumn: Column<ExitCode> = {
  id: "prevented",
  cell: (info) => {
    const exitCode = info.getValue();
    const status = getStatus(exitCode);

    return (
      <div
        className={cn(
          "flex items-center gap-1 whitespace-nowrap font-medium dark:font-normal",
          status === "prevented" && "text-status-green",
          status === "not prevented" && "text-status-red",
          status === "error" && "text-status-yellow",
          status === "not relevant" && "text-secondary-foreground",
        )}
      >
        {status === "prevented" ? <CheckmarkIcon /> : <AlertIcon />}
        <span>{status ? capitalize(status) : "Unavailable"}</span>
        <span>({exitCode})</span>
      </div>
    );
  },
  meta: {
    label: "Prevented",
    widths: ["160px", "1fr"],
  },
};

export const observedResultColumn: Column<string | undefined> = {
  id: "observedResult",
  sortUndefined: "last",
  cell: (info) => {
    const observedResult = info.getValue();
    return (
      <div
        className={cn(
          "flex items-center gap-1 font-medium dark:font-normal",
          observedResult ? "text-status-green" : "text-status-red",
          observedResult === "pending" && "text-secondary-foreground",
        )}
      >
        {observedResult === "pending" ? (
          <RefreshIcon className="size-4" />
        ) : observedResult ? (
          <CheckmarkIcon className="size-4" />
        ) : (
          <CloseIcon className="size-4" />
        )}
        {observedResult === "pending"
          ? "Pending"
          : observedResult
            ? "Yes"
            : "No"}
      </div>
    );
  },
  meta: {
    label: "Observed",
    widths: ["105px", "1fr"],
    authorize: (account) =>
      hasFeatures(account, ["observed_detected"]) &&
      account.controls.some((c) => c.id === ControlCodes.CROWDSTRIKE),
  },
};

export const detectedResultColumn: Column<string | undefined> = {
  id: "detectedResult",
  sortUndefined: "last",
  cell: (info) => {
    const detectedResult = info.getValue();
    return (
      <div
        className={cn(
          "flex items-center gap-1 font-medium dark:font-normal",
          detectedResult ? "text-status-green" : "text-status-red",
          detectedResult === "pending" && "text-secondary-foreground",
        )}
      >
        {detectedResult === "pending" ? (
          <RefreshIcon className="size-4" />
        ) : detectedResult ? (
          <CheckmarkIcon className="size-4" />
        ) : (
          <CloseIcon className="size-4" />
        )}
        {detectedResult === "pending"
          ? "Pending"
          : detectedResult
            ? "Yes"
            : "No"}
      </div>
    );
  },
  meta: {
    label: "Detected",
    widths: ["105px", "1fr"],
    authorize: (account) =>
      hasFeatures(account, ["observed_detected"]) &&
      account.controls.some((c) => c.id === ControlCodes.CROWDSTRIKE),
  },
};

export const expectedResultColumn: Column<ExpectedResult | undefined> = {
  id: "expectedResult",
  sortUndefined: "last",
  enableSorting: true,
  sortingFn: sortColumnObjects,
  cell: (info) => {
    return <ExpectedProtectionLevel result={info.getValue()} />;
  },
  meta: {
    label: "Expected Result",
    widths: ["180px", "1fr"],
    authorize: (account) =>
      hasFeatures(account, ["observed_detected"]) &&
      account.controls.some((c) => c.id === ControlCodes.CROWDSTRIKE),
    exportFn: (data) => data?.expectation ?? "",
  },
};

export const expectedOutcomeColumn: Column<
  ExpectedOutcome | undefined,
  { observed?: string; detected?: string; status: ExitCode }
> = {
  id: "expectedOutcome",
  sortUndefined: "last",
  enableSorting: true,
  sortingFn: sortColumnObjects,
  cell: (info) => (
    <ExpectedOutcomeBlock
      expectedOutcome={info.getValue()}
      status={info.row.original.status}
      observed={info.row.original.observed}
      detected={info.row.original.detected}
    />
  ),
  meta: {
    label: "Expected Outcome",
    widths: ["190px", "1fr"],
    exportFn: (data) => data?.expectation ?? "",
  },
};

export const personalExpectedOutcomeColumn: Column<
  ExpectedResult | undefined,
  { odpFields: OptionalODPFields }
> = {
  id: "personalExpectedOutcome",
  sortUndefined: "last",
  enableSorting: true,
  sortingFn: sortColumnObjects,
  cell: (info) => (
    <PersonalExpectedOutcomeBlock
      expectedResult={info.getValue()}
      odpFields={info.row.original.odpFields}
    />
  ),
  meta: {
    label: "Expected Outcome",
    widths: ["180px", "1fr"],
    exportFn: (data) => data?.expectation ?? "",
  },
};

export const testActionsColumn: Column<string> = {
  id: "test-actions",
  enableSorting: false,
  enableHiding: false,
  cell: (info) => {
    return (
      <div className="flex items-center gap-2">
        <MDPAction id={info.getValue()} />
      </div>
    );
  },
  meta: {
    label: "Detection Actions",
    widths: ["300px", "1fr"],
    canExport: false,
    authorize: (account) =>
      hasFeatures(account, ["detections"]) &&
      account.controls.some(
        (c) =>
          c.id === ControlCodes.CROWDSTRIKE || c.id === ControlCodes.DEFENDER,
      ),
  },
};

export const policySettingNameColumn: Column<
  string,
  object & { excepted?: boolean; control: ControlCode }
> = {
  id: "policy-setting-name",
  enableSorting: true,
  enableHiding: false,
  sortUndefined: "last",
  cell: (info) => (
    <div className="flex items-center gap-1">
      {info.row.original.excepted && (
        <ExceptionIcon className="text-secondary-foreground" />
      )}
      <div
        className={cn(
          "min-w-0",
          info.row.original.excepted
            ? "text-secondary-foreground line-through"
            : undefined,
        )}
      >
        <Truncate text={info.getValue()}>
          <Highlight
            text={info.getValue()}
            highlight={info.table.getState().globalFilter}
          />
        </Truncate>
      </div>
      {info.row.original.control && (
        <PolicySettingsDocLink control={info.row.original.control} />
      )}
    </div>
  ),
  meta: {
    label: "Name",
    widths: ["200px", "3fr"],
  },
};

export const policyLastSeenColumn: Column<string | undefined> = {
  id: "control_last_seen",
  enableSorting: true,
  sortDescFirst: false,
  cell: (info) => {
    const lastSeen = info.getValue();
    if (!lastSeen) {
      return <Unavailable />;
    }
    const date = formatDate(new Date(lastSeen), TimeFormats.DATE_FIRST);
    return <Truncate text={date}>{date}</Truncate>;
  },
  meta: {
    label: "Last Seen",
    widths: ["200px", "1fr"],
    filter: getColumnFilter(lastSeenFilter),
  },
};

export const lastScanColumn: Column<string | undefined> = {
  id: "control_last_scan",
  enableSorting: true,
  sortDescFirst: false,
  cell: (info) => {
    const lastScan = info.getValue();
    if (!lastScan) {
      return <Unavailable />;
    }
    const date = formatDate(new Date(lastScan), TimeFormats.DATE_FIRST);
    return <Truncate text={date}>{date}</Truncate>;
  },
  meta: {
    label: "Last Scan",
    widths: ["200px", "1fr"],
    filter: getColumnFilter(lastScanFilter),
  },
};

export const hasAgentColumn: Column<boolean | undefined> = {
  id: "control_has_agent",
  enableSorting: true,
  sortDescFirst: false,
  cell: (info) => {
    const hasAgent = info.getValue();
    if (hasAgent === undefined) {
      return <Unavailable />;
    }
    const hasAgentText = hasAgent ? "True" : "False";

    return <Truncate text={hasAgentText}>{hasAgentText}</Truncate>;
  },
  meta: {
    label: "Has Agent",
    widths: ["200px", "1fr"],
    filter: getColumnFilter(hasAgentFilter),
  },
};

export const policyOperationalStateColumn: Column<string | undefined> = {
  id: "control_operational_state",
  enableSorting: true,
  sortDescFirst: false,
  cell: (info) => {
    const operationalState = info.getValue();
    if (!operationalState) {
      return <None />;
    }
    const text =
      operationalState === "na"
        ? "N/A"
        : titleCase(operationalState.replaceAll(/[ _]+/g, " "));
    return <Truncate text={text}>{text}</Truncate>;
  },
  meta: {
    label: "Operational State",
    widths: ["200px", "1fr"],
    filter: {
      type: "text",
      label: "Operational State",
      operators: ["is", "is not"],
      odata: {
        id: "operational_state",
        scope: "instances",
      },
      cast: ({ operator, ...filter }) => {
        if (operator === "") {
          throw new Error('Invalid operator: ""');
        }
        const value = filter.value
          .trim()
          .toLowerCase()
          .replaceAll(/[ _]+/g, "_")
          .replaceAll(/\W*/g, "");
        return { ...filter, operator, value };
      },
      parse: (filter) => ({
        ...filter,
        value:
          filter.value === "na"
            ? "N/A"
            : titleCase(String(filter.value).replaceAll(/[ _]+/g, " ")),
        ors: filter.ors?.map((or) => ({
          operator: or.operator,
          value:
            or.value === "na"
              ? "N/A"
              : titleCase(String(or.value).replaceAll(/[ _]+/g, " ")),
        })),
      }),
    },
  },
};

export const policyPlatformColumn: Column<string | undefined> = {
  id: "platform",
  enableSorting: true,
  sortDescFirst: true,
  cell: (info) => {
    const platform = info.getValue();
    if (!platform) {
      return <Unavailable />;
    }
    return (
      <div className="flex min-w-0 items-center gap-1">
        <PlatformIcon
          className="h-4 w-4 text-secondary-foreground"
          platform={platform}
        />
        <Truncate text={platform}>{platform}</Truncate>
      </div>
    );
  },
  meta: {
    label: "Platform",
    widths: ["105px", "1fr"],
    filter: getColumnFilter(platformFilter),
  },
};

export const policySettingCriticalColumn: Column<boolean> = {
  id: "critical",
  enableSorting: true,
  sortDescFirst: true,
  cell: (info) => {
    if (!info.getValue()) {
      return <None />;
    }
    return (
      <div className="bg-status-red/20 px-1 text-status-red">Critical</div>
    );
  },
  meta: {
    label: "Severity",
    widths: ["90px", "1fr"],
  },
};

export const policySettingValueColumn: Column<
  string,
  { value: string; meets_recommendation?: boolean; excepted?: boolean }
> = {
  id: "policy-setting-value",
  enableSorting: true,
  enableHiding: false,
  sortUndefined: "last",
  cell: (info) => {
    const text = capitalize(info.getValue());
    const { excepted, meets_recommendation: meetsRecommendation } =
      info.row.original;

    return (
      <div
        className={cn(
          "min-w-0",
          excepted && "text-secondary-foreground line-through",
          !excepted &&
            meetsRecommendation !== undefined &&
            (meetsRecommendation ? "text-status-green" : "text-status-red"),
        )}
      >
        <Truncate text={text}>{text}</Truncate>
      </div>
    );
  },
  meta: {
    label: "Status",
    widths: ["90px", "1fr"],
  },
};

export const policySettingRecommendationColumn: Column<
  string,
  { recommendation: string; excepted?: boolean }
> = {
  id: "policy-setting-recommendation",
  enableSorting: true,
  enableHiding: false,
  sortUndefined: "last",
  cell: (info) => {
    const text = capitalize(info.getValue());
    return (
      <div
        className={cn(
          "min-w-0",
          info.row.original.excepted
            ? "text-secondary-foreground line-through"
            : undefined,
        )}
      >
        <Truncate text={text}>{text}</Truncate>
      </div>
    );
  },
  meta: {
    label: "Recommendation",
    widths: ["150px", "1fr"],
  },
};

export const policyArrowColumn: DisplayColumnDef<any> = {
  id: "policy-arrow",
  enableSorting: false,
  enableHiding: false,
  header: () => <div />,
  cell: () => <ArrowRight />,
  meta: {
    widths: ["32px", "32px"],
    canExport: false,
  },
};

export const policySettingTechniquesColumn: Column<string[]> = {
  id: "policy-setting-techniques",
  enableSorting: false,
  enableHiding: false,
  cell: (info) => {
    const techniques = info.getValue();
    if (techniques.length === 0) {
      return <None />;
    }
    if (techniques.length === 1) {
      return <Truncate text={techniques[0]}>{techniques[0]}</Truncate>;
    }
    if (techniques.length > 5) {
      return (
        <DefaultTooltip
          content={
            <div className="grid grid-flow-col grid-rows-6 gap-x-4 gap-y-2 text-left text-xs">
              {techniques.map((r) => (
                <span key={r}>{r}</span>
              ))}
            </div>
          }
        >
          {techniques.length} ATT&CK IDs
        </DefaultTooltip>
      );
    }
    return (
      <Truncate
        text={
          <div className="flex items-center gap-2 text-xs">
            {techniques.map((r) => (
              <span key={r}>{r}</span>
            ))}
          </div>
        }
      >
        {techniques.join(", ")}
      </Truncate>
    );
  },
  meta: {
    label: "ATT&CK IDs",
    widths: ["150px", "2fr"],
  },
};

export const endpointHostnameColumn: Column<string | null> = {
  id: "normalized_hostname",
  enableSorting: true,
  sortUndefined: "last",
  cell: (info) => {
    if (!info.getValue()) {
      return <Unavailable />;
    }
    return <Truncate text={info.getValue()}>{info.getValue()}</Truncate>;
  },
  meta: {
    label: "Hostname",
    widths: ["150px", "1fr"],
    filter: getColumnFilter(hostNameFilter),
  },
};

export const endpointSerialColumn: Column<string | null> = {
  id: "normalized_serial_number",
  enableSorting: true,
  sortUndefined: "last",
  cell: (info) => {
    if (!info.getValue()) {
      return <Unavailable />;
    }
    return <Truncate text={info.getValue()}>{info.getValue()}</Truncate>;
  },
  meta: {
    label: "Serial Number",
    widths: ["140px", "1fr"],
    filter: getColumnFilter(serialNumberFilter),
  },
};

export const endpointPrimaryUserColumn: Column<string | undefined> = {
  id: "primary_user_email",
  enableSorting: true,
  sortUndefined: "last",
  cell: (info) => {
    if (!info.getValue()) {
      return <Unavailable />;
    }
    return <Truncate text={info.getValue()}>{info.getValue()}</Truncate>;
  },
  meta: {
    label: "Primary User",
    widths: ["120px", "1fr"],
    filter: getColumnFilter(serialNumberFilter),
  },
};

export const endpointCloudAccountIdColumn: Column<string | null> = {
  id: "normalized_cloud_account_id",
  enableSorting: true,
  sortUndefined: "last",
  cell: (info) => {
    if (!info.getValue()) {
      return <Unavailable />;
    }
    return <Truncate text={info.getValue()}>{info.getValue()}</Truncate>;
  },
  meta: {
    label: "Cloud Account ID",
    widths: ["160px", "1fr"],
    filter: getColumnFilter(cloudAccountIdFilter),
  },
};

export const endpointCloudInstanceIdColumn: Column<string | null> = {
  id: "normalized_cloud_instance_id",
  enableSorting: true,
  sortUndefined: "last",
  cell: (info) => {
    if (!info.getValue()) {
      return <Unavailable />;
    }
    return <Truncate text={info.getValue()}>{info.getValue()}</Truncate>;
  },
  meta: {
    label: "Cloud Instance ID",
    widths: ["160px", "1fr"],
    filter: getColumnFilter(cloudInstanceIdFilter),
  },
};

export const endpointMacAddressColumn: Column<string[] | null> = {
  id: "normalized_mac_address",
  enableSorting: true,
  sortUndefined: "last",
  cell: (info) => {
    const macAddresses = info.getValue();
    if (!macAddresses) {
      return <Unavailable />;
    }
    return (
      <Truncate text={macAddresses.join(", ")}>
        {macAddresses.join(", ")}
      </Truncate>
    );
  },
  meta: {
    label: "MAC Address",
    widths: ["140px", "1fr"],
    filter: getColumnFilter(macAddressFilter),
  },
};

export const policyVersionColumn: Column<string> = {
  id: "version",
  enableSorting: true,
  sortUndefined: "last",
  sortDescFirst: true,
  cell: (info) => {
    const version = info.getValue();
    if (!version) {
      return <Unavailable />;
    }
    return <Truncate text={version}>{version}</Truncate>;
  },
  meta: {
    label: "Version",
    widths: ["90px", "1fr"],
    filter: getColumnFilter(versionFilter),
  },
};

export const endpointDeviceTypeColumn: Column<string | null> = {
  id: "control_device_type",
  enableSorting: true,
  sortUndefined: "last",
  sortDescFirst: false,
  cell: (info) => {
    const deviceType = info.getValue();
    if (!deviceType) {
      return <Unavailable />;
    }
    return <Truncate text={deviceType}>{deviceType}</Truncate>;
  },
  meta: {
    label: "Device Type",
    widths: ["120px", "1fr"],
    filter: getColumnFilter(deviceTypeFilter),
  },
};

export const userTypeColumn: Column<string | null> = {
  id: "control_user_type",
  enableSorting: true,
  sortUndefined: "last",
  sortDescFirst: false,
  cell: (info) => {
    const userType = info.getValue();
    if (!userType) {
      return <Unavailable />;
    }
    return <Truncate text={userType}>{userType}</Truncate>;
  },
  meta: {
    label: "User Type",
    widths: ["120px", "1fr"],
    filter: getColumnFilter(userTypeFilter),
  },
};

export const cloudRegionColumn: Column<string | null> = {
  id: "control_cloud_region",
  enableSorting: true,
  sortUndefined: "last",
  sortDescFirst: false,
  cell: (info) => {
    const cloudRegion = info.getValue();
    if (!cloudRegion) {
      return <Unavailable />;
    }
    return <Truncate text={cloudRegion}>{cloudRegion}</Truncate>;
  },
  meta: {
    label: "Cloud Region",
    widths: ["120px", "1fr"],
    filter: getColumnFilter(controlCloudRegionFilter),
  },
};

export const cloudAccountIdColumn: Column<string | null> = {
  id: "control_cloud_account_id",
  enableSorting: true,
  sortUndefined: "last",
  sortDescFirst: false,
  cell: (info) => {
    const cloudAccountId = info.getValue();
    if (!cloudAccountId) {
      return <Unavailable />;
    }
    return <Truncate text={cloudAccountId}>{cloudAccountId}</Truncate>;
  },
  meta: {
    label: "Cloud Account ID",
    widths: ["120px", "1fr"],
    filter: getColumnFilter(controlCloudAccountIdFilter),
  },
};

export const cloudInstanceIdColumn: Column<string | null> = {
  id: "control_cloud_instance_id",
  enableSorting: true,
  sortUndefined: "last",
  sortDescFirst: false,
  cell: (info) => {
    const cloudInstanceId = info.getValue();
    if (!cloudInstanceId) {
      return <Unavailable />;
    }
    return <Truncate text={cloudInstanceId}>{cloudInstanceId}</Truncate>;
  },
  meta: {
    label: "Cloud Instance ID",
    widths: ["120px", "1fr"],
    filter: getColumnFilter(controlCloudInstanceIdFilter),
  },
};

export const inboxTypeColumn: Column<string | null> = {
  id: "control_inbox_type",
  enableSorting: true,
  sortUndefined: "last",
  sortDescFirst: false,
  cell: (info) => {
    const inboxType = info.getValue();
    if (!inboxType) {
      return <Unavailable />;
    }
    return <Truncate text={inboxType}>{inboxType}</Truncate>;
  },
  meta: {
    label: "Inbox Type",
    widths: ["120px", "1fr"],
    filter: getColumnFilter(inboxTypeFilter),
  },
};

export const policyEndpointOwnerColumn: Column<string | undefined> = {
  id: "control_device_ownership",
  enableSorting: true,
  sortUndefined: "last",
  cell: (info) => {
    const ownership = info.getValue();
    if (!ownership) {
      return <Unavailable />;
    }
    return <Truncate text={ownership}>{ownership}</Truncate>;
  },
  meta: {
    label: "Ownership",
    widths: ["120px", "1fr"],
    filter: getColumnFilter(ownershipFilter),
  },
};

export const noEDREndpointReasonColumn: Column<string> = {
  id: "no_edr_reason",
  enableSorting: false,
  sortUndefined: "last",
  cell: (info) => {
    if (!info.getValue()) {
      return <Unavailable />;
    }
    return <Truncate text={info.getValue()}>{info.getValue()}</Truncate>;
  },
  meta: {
    label: "Reason",
    widths: ["100px", "1fr"],
  },
};

export const statusColumn: Column<string | undefined> = {
  id: "control_status",
  enableSorting: true,
  sortUndefined: "last",
  sortDescFirst: true,
  cell: (info) => {
    const status = info.getValue();
    if (!status) {
      return <Unavailable />;
    }
    return <Truncate text={status}>{status}</Truncate>;
  },
  meta: {
    label: "Status",
    widths: ["100px", "1fr"],
    filter: getColumnFilter(statusFilter),
  },
};

export const riskColumn: Column<string | undefined> = {
  id: "control_risk",
  enableSorting: true,
  sortUndefined: "last",
  sortDescFirst: true,
  cell: (info) => {
    const risk = info.getValue();
    if (!risk) {
      return <Unavailable />;
    }
    return <Truncate text={risk}>{risk}</Truncate>;
  },
  meta: {
    label: "Risk",
    widths: ["100px", "1fr"],
    filter: getColumnFilter(riskFilter),
  },
};

export const controlDomainColumn: Column<string | undefined> = {
  id: "control_domain",
  enableSorting: true,
  sortUndefined: "last",
  cell: (info) => {
    const domain = info.getValue();
    if (!domain) {
      return <Unavailable />;
    }
    return <Truncate text={domain}>{domain}</Truncate>;
  },
  meta: {
    label: "Domain",
    widths: ["250px", "1fr"],
    filter: getColumnFilter(domainFilter),
  },
};

export const controlPrincipalNameColumn: Column<string | undefined> = {
  id: "control_principal_name",
  enableSorting: true,
  sortUndefined: "last",
  cell: (info) => {
    const principal_name = info.getValue();
    if (!principal_name) {
      return <Unavailable />;
    }
    return <Truncate text={principal_name}>{principal_name}</Truncate>;
  },
  meta: {
    label: "Principal Name",
    widths: ["250px", "1fr"],
    filter: getColumnFilter(principalNameFilter),
  },
};

export const exceptedColumn: Column<ExceptedCategory[]> = {
  id: "excepted",
  enableSorting: true,
  sortDescFirst: true,
  cell: (info) => {
    const excepted = info.getValue();
    const relevantExceptions = excepted.filter((e) => e.excepted);

    if (relevantExceptions.length === 0) {
      return <None />;
    }

    const exceptions = relevantExceptions
      ?.map((exception) => getControlCategoryName(exception.category))
      .join(", ");

    return (
      <span className="flex items-center gap-1">
        <ExceptionIcon />
        <Truncate text={exceptions}>{exceptions}</Truncate>
      </span>
    );
  },
  meta: {
    label: "Exceptions",
    widths: ["120px", "1fr"],
    filter: getColumnFilter(exceptedFilter),
  },
};

export const controlFailureColumn: Column<string, any, "start_time"> = {
  id: "start_time",
  enableSorting: true,
  sortDescFirst: true,
  sortUndefined: "last",
  cell: (info) => {
    const value = info.getValue();
    if (value === undefined) {
      return <Unavailable />;
    }

    const displayDays = differenceInDays(new Date(), new Date(value));

    return (
      <DefaultTooltip
        content={`Start time: ${formatDate(new Date(value), TimeFormats.DATE_FIRST)}`}
      >
        {displayDays} {pluralize(displayDays, "day")}
      </DefaultTooltip>
    );
  },
  meta: {
    label: startTimeFilter.label,
    widths: ["130px", "1fr"],
    filter: getColumnFilter(durationStartTimeFilter),
  },
};

export function odpColumns<T extends OptionalODPFields>(
  helper: ColumnHelper<T>,
) {
  return [
    helper.accessor((row) => row.observed, defaultHidden(observedColumn)),
    helper.accessor((row) => row.detected, defaultHidden(detectedColumn)),
    helper.accessor((row) => row.prevented, defaultHidden(preventedColumn)),
  ];
}

export const sortByField = (column?: ColumnSort, odata?: OdataDefinition) => {
  if (!column || !odata) return undefined;

  const direction = column.desc ? "desc" : "asc";

  if (odata.scope && odata.with) {
    const withs = Object.entries(odata.with)
      .filter(([, value]) => value !== undefined)
      .map(([field, value]) => `${field} eq ${getEncodedOdataValue(value)}`)
      .join(" and ");

    return `where(${odata.scope}, ${withs}, ${odata.id}) ${direction} nulls last`;
  }

  if (Array.isArray(odata.sort)) {
    const sort = odata.sort.map((sort) => `${sort} ${direction}`).join(", ");
    return `(${sort}) nulls last`;
  }

  return `${odata.sort ?? odata.id} ${direction} nulls last`;
};

export function getColumnId(column: Column<any>) {
  return column.meta?.filter?.odata
    ? getOdataId(column.meta?.filter?.odata)
    : column.id;
}

export function getColumnWith<T>(
  column: Column<T>,
  withs: OdataDefinition["with"],
): Column<T> {
  const withColumn = {
    ...column,
    meta: {
      ...column.meta,
      filter: {
        ...column.meta?.filter,
        odata: { ...column.meta?.filter?.odata, with: withs },
      },
    },
  } as Column<any>;

  if (withColumn.meta?.filter?.odata) {
    withColumn.id = getColumnId(withColumn);
  }
  return withColumn;
}

export function getColumnWithControl<T>(
  column: Column<T>,
  control: ValidControlCode,
  instanceId: string,
): Column<T> {
  return getColumnWith(column, { control, instance_id: instanceId });
}

export function getColumnWithControlCategory<T>(
  column: Column<T>,
  category: ValidControlCategory,
): Column<T> {
  return getColumnWith(column, { category });
}

export function defaultHidden<T>(column: Column<T>, hidden = true): Column<T> {
  return { ...column, meta: { ...column.meta, defaultHidden: hidden } };
}

export function getColumnVisibility(
  columns: ColumnDef<any, any>[],
): VisibilityState {
  const hiddenColumns = columns.filter((col) => col.meta?.defaultHidden);
  return Object.fromEntries(hiddenColumns.map((col) => [col.id, false]));
}

export function getExpectedRowClassName<T>(row: Row<T>): string {
  const cells = row.getAllCells();
  const exceptedRowId = cells.find((cell) =>
    cell.column.id.includes("excepted"),
  )?.column.id;

  return exceptedRowId &&
    (row.getValue(exceptedRowId) as ExceptedCategory[]).some((e) => e.excepted)
    ? "text-secondary-foreground line-through"
    : "";
}
